import { font } from '@services/MarppleShop/shared/typography';
import { html, Page, View } from 'rune-ts';
import { isApp } from '../../../shared/app/util';
import klass from './PlusShopProductList.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { Dropdown, DropdownChangeEvent } from '../../components/cells/Dropdown/Dropdown';
import { Pagination, PaginationChangeEvent } from '../../components/cells/Pagination/Pagination';
import { PaginationData } from '../../../features/Pagination/type';
import { classes, htmlIf, updateUrlParams } from '../../../shared/util';
import { ProductCardViewData, ProductCardSize, SortOption } from '../../../features/ProductList/type';
import { PRODUCT_LIST_SORT } from '../../../features/ProductList/constant';
import { ProductCardListView } from '../../components/cells/ProductCardList/ProductCardListView.ts';
import { DOCUMENT_EVENT, listenDocumentEvent } from '../../../shared/util/event';
import { getGnbMenu } from '../../../shared/constant';
import { ButtonCheckbox } from '../../components/atoms/ButtonCheckbox/ButtonCheckbox';
import {
  CreatorShopProductListEmptyScreen,
  PlusShopProductListEmptyScreen,
} from '../../components/cells/ProductCardList/ProductCardListEmptyScreen';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { generateProductListUrl } from '../../../features/ProductList/util';
import { Tabs } from '../../components/cells/Tabs/Tabs';
import { loading } from '../../../shared/client/global';
import { ProductListCount } from '../ProductList/view/ProductListCount';
import { SectionTitle } from '../../components/cells/SectionTitle/SectionTitle';
import { SubHeaderData } from '@services/MarppleShop/renderApp/components/cells/Header/libs/types/headerSub';

export interface PlusShopProductListPageData {
  products: ProductCardViewData[];
  count: number;
  pagination: PaginationData;
  sort: string;
  sort_options: SortOption[];
  cate_list_id?: number;
  cate_item_id?: number;
  type?: 'best' | 'limited';
  title: string;
  sub_title?: string;
  without_sold_out?: boolean;
  plus_name?: string;
  domain_name?: string;
  layout_options: BaseLayoutOptions;
  cate_lists: SubHeaderData['menus'];
}

export class PlusShopProductListBodyView extends View<PlusShopProductListPageData> {
  PaginationView: Pagination;
  ProductCardListView: ProductCardListView;
  ProductListCountView: ProductListCount | null = null;
  ExcludeSoldOutCheckboxView: ButtonCheckbox;

  BEST_TAB_KEY: Readonly<string>;
  LIMITED_EDITION_TAB_KEY: Readonly<string>;

  static LIMIT_BEST: Readonly<number> = 50;
  static LIMIT_DEFAULT: Readonly<number> = 60;

  constructor(data: PlusShopProductListPageData) {
    super(data);
    this.PaginationView = new Pagination(this.data.pagination, { klass: klass.pagination });

    this.ProductCardListView = new ProductCardListView(this.data, {
      resize_width: 600,
      size: this.data.layout_options.is_mobile ? ProductCardSize.small : ProductCardSize.large,
      is_lazy: true,
      container_klass: klass.product_cards_container,
    });

    this.ProductCardListView.setEmptyListView(
      this.isPlusShop()
        ? new PlusShopProductListEmptyScreen({ plus_name: this.data.plus_name })
        : new CreatorShopProductListEmptyScreen(),
    );

    this.BEST_TAB_KEY = getGnbMenu('best').key;
    this.LIMITED_EDITION_TAB_KEY = getGnbMenu('limited').key;

    if (this.isCountVisible()) {
      this.ProductListCountView = new ProductListCount({
        count: this.data.count,
        is_mobile: this.data.layout_options.is_mobile,
      });
    }

    this.ExcludeSoldOutCheckboxView = new ButtonCheckbox({
      active: this.data.without_sold_out,
      label: this.data.layout_options.is_mobile
        ? ET('mps2::product::filter::without_sold_out_short')
        : ET('mps2::product::filter::without_sold_out'),
      name: 'without_sold_out',
      is_mobile: this.data.layout_options.is_mobile,
    });
  }

  protected override onRender() {
    // only mobile
    // 카테고리 헤더 변경 이벤트
    if (this.data.layout_options.is_mobile) {
      listenDocumentEvent(DOCUMENT_EVENT.sub_header_menu_change, async (event) => {
        const e = event as CustomEvent<{
          cate_list_id?: string;
          cate_item_id?: string;
          menu: SubHeaderData['menus'][0];
        }>;
        const { menu, cate_item_id, cate_list_id } = e.detail;

        if (!isApp()) loading();

        if (cate_item_id) {
          location.href = updateUrlParams(location.href, {
            cate_list_id,
            cate_item_id,
            page: 1,
            type: undefined,
          });
          return;
        }

        location.href = menu.url;
      });
    }

    this.delegate(PaginationChangeEvent, Pagination, async (e) => {
      location.replace(updateUrlParams(location.href, { page: e.detail.page }));
    });

    this.delegate(DropdownChangeEvent<keyof typeof PRODUCT_LIST_SORT>, Dropdown, async (e, _view) => {
      const sort = e.detail.option?.key;
      location.replace(updateUrlParams(location.href, { sort, page: 1 }));
    });

    // "품절 상품 제외" 체크박스 이벤트
    this.delegate('change', ButtonCheckbox, (_e, view) => {
      const without_sold_out = view.isChecked() ? 'true' : undefined;
      location.replace(updateUrlParams(location.href, { without_sold_out, page: 1 }));
    });
  }

  // "정렬 드롭다운" 표시 할건지
  private isSortVisible(): boolean {
    const { type } = this.data;
    return type !== this.BEST_TAB_KEY && type !== this.LIMITED_EDITION_TAB_KEY;
  }

  // "품절 상품 제외" 체크박스 표시 할건지
  private isExcludeSoldOutCheckboxVisible(): boolean {
    return this.data.type !== this.BEST_TAB_KEY;
  }

  private isPlusShop(): this is { data: PlusShopProductListPageData & { plus_name: string } } {
    return !!this.data.plus_name;
  }

  protected override template() {
    const {
      title,
      layout_options: { is_mobile },
      type,
      cate_lists,
      cate_list_id,
      cate_item_id,
      domain_name,
    } = this.data;
    const is_best_tab = type === this.BEST_TAB_KEY;

    const cate_item_tabs = cate_lists
      .filter((cate_list) => +cate_list.key === cate_list_id)
      .flatMap((cate_list) => {
        return [
          {
            cate_list_id: +cate_list.key,
            id: undefined,
            name: ET('mps2::product::all_products'),
          },
          ...(cate_list.sub_menus || [])
            .filter((menu) => {
              // 전체 메뉴를 주입하는 쪽에서 넣고 있어서 거름
              return !Number.isNaN(+menu.key);
            })
            .map((menu) => ({
              cate_list_id: +cate_list.key,
              id: +menu.key,
              name: menu.name,
            })),
        ];
      })
      .filter((cate_item) => cate_item.name)
      .map((cate_item) => {
        return {
          id: cate_item.id ? cate_item.id.toString() : getGnbMenu('all_products').key,
          href: generateProductListUrl({
            base_url: domain_name ? `/${ET.lang}/${domain_name}/products` : undefined,
            cate_list_id: cate_item.cate_list_id as number | undefined,
            cate_item_id: cate_item.id,
          }),
          label: cate_item.name as string,
        };
      });

    const container_class = classes({
      [klass.container]: true,
      [klass.creator_shop]: !this.isPlusShop(),
    });

    return html`
      <div class="${container_class}">
        ${!this.isPlusShop() && !is_mobile
          ? html`<div class="${klass.category_header_container}">
              <div class="${klass.category_header}">
                ${cate_lists.map((category) => {
                  const item_class = classes({
                    [klass.category]: true,
                    [font(category.is_selected ? '16_bold' : '16_medium')]: true,
                    [klass.selected]: category.is_selected,
                  });
                  return html`<a class="${item_class}" href="${category.url}">${category.name}</a>`;
                })}
              </div>
            </div>`
          : ''}
        <div class="${klass.title_container}">
          ${new SectionTitle({
            title_props: {
              title,
              size: is_mobile ? 'large' : 'xLarge',
            },
          })}
          ${htmlIf(
            this.ExcludeSoldOutCheckboxView,
            type === this.LIMITED_EDITION_TAB_KEY && this.isExcludeSoldOutCheckboxVisible(),
          )}
        </div>
        ${htmlIf(
          new Tabs({
            tabs: cate_item_tabs,
            current_tab_id: cate_item_id ? cate_item_id.toString() : getGnbMenu('all_products').key,
            custom_klass: klass.tabs,
          }),
          !is_mobile && cate_item_tabs.length > 0,
        )}

        <div class="${klass.body}">
          <div class="${klass.product_section}">
            <div class="${klass.sort_container}">
              ${this.ProductListCountView ?? ''}
              <div class="${klass.right_panel}">
                ${htmlIf(
                  this.ExcludeSoldOutCheckboxView,
                  type !== this.LIMITED_EDITION_TAB_KEY && this.isExcludeSoldOutCheckboxVisible(),
                )}
                ${htmlIf(
                  new Dropdown(
                    {
                      options: this.data.sort_options,
                      selected_option_key: this.data.sort ?? this.data.sort_options[0]?.key,
                    },
                    {
                      horizontal: 'right',
                      klass: klass.sort_container,
                      without_container: is_mobile,
                      fit_width_to_longest_name: true,
                      text_align: is_mobile ? 'right' : 'left',
                    },
                  ),
                  this.isSortVisible(),
                )}
              </div>
            </div>
            ${this.ProductCardListView}${htmlIf(this.PaginationView, !is_best_tab)}
          </div>
        </div>
      </div>
    `;
  }

  private isCountVisible(): boolean {
    return this.data.type !== this.BEST_TAB_KEY && this.data.type !== this.LIMITED_EDITION_TAB_KEY;
  }
}

export class PlusShopProductListPage extends Page<PlusShopProductListPageData> {
  protected override template() {
    const { layout_options } = this.data;

    return html`
      <div>
        ${new LayoutView(
          {
            body: new PlusShopProductListBodyView(this.data),
            footer_data: { ...footer_links, lang: ET.lang, is_mobile: layout_options.is_mobile },
          },
          {
            require_top_arrow_button: true,
            has_bottom_tab_bar: true,
            selected_bottom_tab_idx: -1,
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
